/**
 * @file Определения фильтров каталога.
 * Каждое определение описывает: как читать значение из DOM-атрибута элемента,
 * как нормализовать значение из формы фильтров, и какой оператор применять для сравнения.
 */

import { greaterThanOrEqual, includesAny } from './filter_operators'
import { matchesWorkingHours, parseWorkingHours } from './working_hours_filter'

/**
 * Извлекает массив строковых значений из объектов фильтра.
 * @param {Array<string|{value: string}>} filterValues
 * @returns {string[]}
 */
function selectedValues(filterValues) {
  return filterValues.map((filterValue) => {
    if (filterValue && typeof filterValue === 'object') {
      return String(filterValue.value)
    }

    return String(filterValue)
  })
}

/**
 * Преобразует значения фильтра в Set строк.
 * @param {Array<string|{value: string}>} filterValues
 * @returns {Set<string>}
 */
function selectedValuesSet(filterValues) {
  return new Set(selectedValues(filterValues))
}

/**
 * Разбирает строку значений из data-атрибута элемента (через запятую) в Set.
 * @param {string|null} rawValue - Значение data-атрибута, например `"indoor,outdoor"`.
 * @returns {Set<string>}
 */
function dataListSet(rawValue) {
  if (!rawValue) return new Set()

  return new Set(
    rawValue
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  )
}

/**
 * Преобразует строку из data-атрибута в число. Возвращает null при некорректном значении.
 * @param {string|null} rawValue
 * @returns {number|null}
 */
function numberValue(rawValue) {
  if (rawValue === null || rawValue.trim() === '') return null

  const value = Number(rawValue)

  return Number.isFinite(value) ? value : null
}

/**
 * Возвращает минимальное числовое значение из выбранных значений фильтра.
 * Используется, например, для фильтра «рейтинг не ниже».
 * @param {Array<string|{value: string}>} filterValues
 * @returns {number|null}
 */
function minimumSelectedNumber(filterValues) {
  const values = selectedValues(filterValues)
    .map(Number)
    .filter(Number.isFinite)

  return values.length > 0 ? Math.min(...values) : null
}

/**
 * Реестр определений фильтров каталога.
 * Ключ `default` — базовое определение; остальные ключи переопределяют его для конкретных фильтров.
 * Каждое определение содержит:
 * - `itemAttribute` — имя data-атрибута на DOM-узле элемента каталога.
 * - `parseItemValue` — парсит значение атрибута в удобный для сравнения формат.
 * - `normalizeFilterValue` — нормализует значение из формы фильтров.
 * - `matches` — оператор сравнения.
 * - `getItemValue` (опционально) — кастомный геттер значения из модели элемента.
 * @type {Object.<string, object>}
 */
const filterDefinitions = {
  default: {
    parseItemValue: dataListSet,
    normalizeFilterValue: selectedValuesSet,
    matches: includesAny
  },

  rating: {
    itemAttribute: 'data-rating',
    parseItemValue: numberValue,
    normalizeFilterValue: minimumSelectedNumber,
    matches: greaterThanOrEqual
  },

  // eslint-disable-next-line camelcase
  working_hours: {
    getItemValue(item, filterName) {
      return {
        schedule: item[filterName],
        timeZone: item.timeZone
      }
    },
    parseItemValue: parseWorkingHours,
    normalizeFilterValue: selectedValuesSet,
    matches: matchesWorkingHours
  }
}

/**
 * Возвращает полное определение фильтра по его имени.
 * Объединяет базовое определение `default` с конкретным (если задано),
 * автоматически вычисляя `itemAttribute` как `data-filter-{filterName}`.
 * @param {string} filterName - Имя фильтра (например, `"rating"`, `"court_types"`).
 * @returns {object} Полное определение фильтра.
 */
function getFilterDefinition(filterName) {
  return {
    ...filterDefinitions.default,
    itemAttribute: `data-filter-${filterName}`,
    ...filterDefinitions[filterName]
  }
}

export { filterDefinitions, getFilterDefinition }
